import { format } from "date-fns"
import { it } from "date-fns/locale"
import { FileText, Image, FileSpreadsheet, Archive } from "lucide-react"
import ReactMarkdown from "react-markdown"
import { DocumentCitation } from "./document-citation"
import remarkGfm from "remark-gfm"
import remarkBreaks from "remark-breaks"

interface AttachmentInfo {
  name: string
  type: string
  size: number
  url?: string
}

interface MessageBubbleProps {
  type: "user" | "agent"
  content: string
  timestamp: Date
  attachments?: AttachmentInfo[]
  /** Tool currently being executed by the agent (only set while streaming) */
  toolCall?: string
  /** Whether the agent message is still being produced */
  isStreaming?: boolean
  onDocumentOpen?: (path: string) => void
  citations?: Array<{ node_id: number; name?: string | null }>
}

function renderCitations(content: string, onOpen?: (path: string) => void) {
  // Il modello presenta normalmente le fonti come "**nome file** [node_id]".
  // Consuma anche il grassetto, così è il nome leggibile (non il solo ID)
  // a diventare il controllo che apre il documento.
  const citation = /(\*\*([^*\n]+)\*\*\s*)?\[\s*(?:node[\s_]*id\s*[=:]?\s*)?(\d+)\s*\]/gi
  const rendered: React.ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = citation.exec(content)) !== null) {
    if (match.index > cursor) {
      rendered.push(
        <ReactMarkdown key={`md-${cursor}`} remarkPlugins={[remarkGfm, remarkBreaks]}>
          {content.slice(cursor, match.index)}
        </ReactMarkdown>,
      )
    }
    rendered.push(
      <DocumentCitation
        key={`citation-${match[3]}-${match.index}`}
        nodeId={Number(match[3])}
        label={match[2]?.trim()}
        onOpen={onOpen}
      />,
    )
    cursor = citation.lastIndex
  }

  if (cursor < content.length) {
    rendered.push(
      <ReactMarkdown key={`md-${cursor}`} remarkPlugins={[remarkGfm, remarkBreaks]}>
        {content.slice(cursor)}
      </ReactMarkdown>,
    )
  }

  return rendered
}

function uncitedStructuredSources(content: string, citations: Array<{ node_id: number; name?: string | null }>) {
  const inlineIds = new Set<number>()
  const pattern = /\[\s*(?:node[\s_]*id\s*[=:]?\s*)?(\d+)\s*\]/gi
  let match: RegExpExecArray | null
  while ((match = pattern.exec(content)) !== null) inlineIds.add(Number(match[1]))
  return citations.filter((citation) => !inlineIds.has(citation.node_id))
}

// Human-readable Italian labels for known tools.
// Tools not listed here fall back to a verb+noun Italian translation.
const TOOL_LABELS: Record<string, string> = {
  list_contacts: "Ricerca contatti",
  get_contact_detail: "Apertura contatto",
  list_tags: "Lettura etichette",
  create_tag: "Creazione etichetta",
  delete_tag: "Eliminazione etichetta",
  update_contact: "Aggiornamento contatto",
  delete_contact: "Eliminazione contatto",
  list_contact_documents: "Lettura documenti",
  list_documents: "Lettura documenti",
  get_document_metadata: "Lettura documento",
  find_documents: "Ricerca documenti",
  search_documents: "Ricerca documenti",
  read_document: "Lettura documento",
  preview_document_text: "Lettura documento",
  summarize_document: "Riassunto documento",
  upload_document: "Caricamento documento",
  upload_document_from_chat: "Caricamento documento",
  upload_document_from_url: "Caricamento documento",
  move_document: "Spostamento documento",
  rename_document: "Rinomina documento",
  delete_document: "Eliminazione documento",
  delete_contact_document: "Eliminazione documento",
  create_contact_folder: "Creazione cartella",
  list_appointments: "Ricerca appuntamenti",
  get_appointment_detail: "Apertura appuntamento",
  list_appointment_history: "Storico appuntamento",
  update_appointment: "Aggiornamento appuntamento",
  delete_appointment: "Eliminazione appuntamento",
  create_appointment: "Creazione appuntamento",
  list_tasks: "Ricerca attività",
  get_task_detail: "Apertura attività",
  create_task: "Creazione attività",
  update_task: "Aggiornamento attività",
  delete_task: "Eliminazione attività",
  list_users: "Ricerca utenti",
  get_my_profile: "Lettura profilo",
  list_users_status: "Stato utenti",
  update_my_profile: "Aggiornamento profilo",
  web_search: "Ricerca sul web",
  knowledge_search: "Ricerca conoscenze",
}

// English verb prefix -> Italian action noun (used by the fallback)
const TOOL_VERBS: Record<string, string> = {
  list: "Ricerca",
  get: "Apertura",
  fetch: "Recupero",
  find: "Ricerca",
  search: "Ricerca",
  read: "Lettura",
  view: "Apertura",
  show: "Apertura",
  preview: "Anteprima",
  summarize: "Riassunto",
  create: "Creazione",
  add: "Creazione",
  new: "Creazione",
  make: "Creazione",
  update: "Aggiornamento",
  edit: "Modifica",
  modify: "Modifica",
  set: "Impostazione",
  change: "Modifica",
  rename: "Rinomina",
  move: "Spostamento",
  copy: "Copia",
  duplicate: "Duplicazione",
  merge: "Unione",
  split: "Divisione",
  delete: "Eliminazione",
  remove: "Rimozione",
  destroy: "Eliminazione",
  archive: "Archiviazione",
  restore: "Ripristino",
  upload: "Caricamento",
  download: "Download",
  import: "Importazione",
  export: "Esportazione",
  send: "Invio",
  receive: "Ricezione",
  share: "Condivisione",
  assign: "Assegnazione",
  generate: "Generazione",
  count: "Conteggio",
  check: "Verifica",
  validate: "Validazione",
  verify: "Verifica",
  calculate: "Calcolo",
  analyze: "Analisi",
  parse: "Elaborazione",
  convert: "Conversione",
  format: "Formattazione",
  run: "Esecuzione",
  execute: "Esecuzione",
  start: "Avvio",
  stop: "Arresto",
  pause: "Pausa",
  resume: "Ripresa",
  cancel: "Annullamento",
  close: "Chiusura",
  open: "Apertura",
  save: "Salvataggio",
  log: "Registrazione",
  notify: "Notifica",
  book: "Prenotazione",
  schedule: "Pianificazione",
  plan: "Pianificazione",
  complete: "Completamento",
  finish: "Completamento",
}

// English noun (singular/plural) -> Italian noun (singular).
// Keys are matched case-insensitively against remaining tokens.
const TOOL_NOUNS: Record<string, string> = {
  contact: "contatto",
  contacts: "contatti",
  lead: "lead",
  leads: "lead",
  client: "cliente",
  clients: "clienti",
  customer: "cliente",
  company: "azienda",
  companies: "aziende",
  account: "account",
  task: "attività",
  tasks: "attività",
  todo: "attività",
  appointment: "appuntamento",
  appointments: "appuntamenti",
  meeting: "riunione",
  meetings: "riunioni",
  event: "evento",
  events: "eventi",
  calendar: "calendario",
  document: "documento",
  documents: "documenti",
  doc: "documento",
  docs: "documenti",
  file: "file",
  files: "file",
  folder: "cartella",
  folders: "cartelle",
  directory: "cartella",
  tag: "etichetta",
  tags: "etichette",
  label: "etichetta",
  labels: "etichette",
  category: "categoria",
  categories: "categorie",
  user: "utente",
  users: "utenti",
  member: "membro",
  members: "membri",
  profile: "profilo",
  account_profile: "profilo",
  note: "nota",
  notes: "note",
  message: "messaggio",
  messages: "messaggi",
  email: "email",
  emails: "email",
  notification: "notifica",
  notifications: "notifiche",
  alert: "avviso",
  alerts: "avvisi",
  reminder: "promemoria",
  reminders: "promemoria",
  call: "chiamata",
  calls: "chiamate",
  invoice: "fattura",
  invoices: "fatture",
  quote: "preventivo",
  quotes: "preventivi",
  estimate: "preventivo",
  order: "ordine",
  orders: "ordini",
  product: "prodotto",
  products: "prodotti",
  deal: "affare",
  deals: "affari",
  opportunity: "opportunità",
  opportunities: "opportunità",
  pipeline: "pipeline",
  stage: "fase",
  project: "progetto",
  projects: "progetti",
  report: "report",
  reports: "report",
  attachment: "allegato",
  attachments: "allegati",
  history: "storico",
  log: "registro",
  status: "stato",
  detail: "dettaglio",
  details: "dettagli",
  metadata: "metadati",
  text: "testo",
  content: "contenuto",
  url: "link",
  chat: "chat",
  knowledge: "conoscenze",
  web: "web",
  data: "dati",
  setting: "impostazione",
  settings: "impostazioni",
  preference: "preferenza",
  preferences: "preferenze",
  role: "ruolo",
  roles: "ruoli",
  permission: "permesso",
  permissions: "permessi",
}

function toolLabel(tool: string): string {
  const known = TOOL_LABELS[tool]
  if (known) return known

  const tokens = tool.split(/[_\s]+/).filter(Boolean)
  if (tokens.length === 0) return tool

  // First token = verb prefix
  const verbKey = tokens[0].toLowerCase()
  const verb = TOOL_VERBS[verbKey]

  // Remaining tokens = noun (object of the action)
  const nounTokens = tokens.slice(1)
  const noun = nounTokens
    .map((t) => TOOL_NOUNS[t.toLowerCase()] ?? null)
    .filter((n): n is string => n !== null)
    .join(" ")

  if (verb && noun) return `${verb} ${noun}`
  if (verb) return verb
  if (noun) return noun.charAt(0).toUpperCase() + noun.slice(1)

  // Last resort: humanize snake_case without translating
  return tool
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function AttachmentIcon({ type }: { type: string }) {
  if (type.startsWith("image/")) return <Image className="w-3 h-3 flex-shrink-0" />
  if (type.includes("spreadsheet") || type.includes("csv") || type.includes("excel"))
    return <FileSpreadsheet className="w-3 h-3 flex-shrink-0" />
  if (type.includes("zip") || type.includes("rar") || type.includes("tar") || type.includes("7z") || type.includes("archive"))
    return <Archive className="w-3 h-3 flex-shrink-0" />
  return <FileText className="w-3 h-3 flex-shrink-0" />
}

function truncateName(name: string, max = 28): string {
  if (name.length <= max) return name
  const ext = name.lastIndexOf(".")
  if (ext > 0 && name.length - ext <= 6) {
    const base = name.slice(0, max - 3 - (name.length - ext))
    return `${base}...${name.slice(ext)}`
  }
  return `${name.slice(0, max - 3)}...`
}

export function MessageBubble({ type, content, timestamp, attachments, toolCall, isStreaming, onDocumentOpen, citations = [] }: MessageBubbleProps) {
  const isUser = type === "user"
  const showToolBadge = !isUser && isStreaming && !!toolCall
  const showThinking = !isUser && isStreaming && !content && !toolCall
  const showStreamingCursor = !isUser && isStreaming && !!content && !toolCall
  const structuredSources = uncitedStructuredSources(content, citations)

  return (
    <div
      className="flex min-w-0 flex-col"
      style={{ alignSelf: isUser ? "flex-end" : "flex-start", maxWidth: isUser ? "80%" : "85%" }}
    >
      <div
        className={isUser ? "max-w-full overflow-x-auto" : "agent-markdown max-w-full overflow-x-auto"}
        style={{
          backgroundColor: isUser ? "var(--hexa-purple)" : "var(--hexa-surface-hover)",
          color: isUser ? "#ffffff" : "var(--hexa-text-primary)",
          borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          padding: "10px 14px",
          fontSize: "13px",
          lineHeight: "1.4",
          ...(isUser ? { whiteSpace: "pre-wrap" as const, wordBreak: "break-word" as const } : { wordBreak: "break-word" as const }),
        }}
      >
        {isUser ? (
          content
        ) : showThinking ? (
          <span style={{ color: "var(--hexa-text-muted)" }}>Pensando...</span>
        ) : (
          <>
            {renderCitations(content, onDocumentOpen)}
            {structuredSources.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" aria-label="Fonti documentali">
                {structuredSources.map((citation) => (
                  <DocumentCitation key={citation.node_id} nodeId={citation.node_id} label={citation.name || undefined} onOpen={onDocumentOpen} />
                ))}
              </div>
            )}
            {showStreamingCursor && (
              <span
                className="inline-block ml-0.5"
                style={{
                  width: 6,
                  height: 13,
                  verticalAlign: "text-bottom",
                  backgroundColor: "var(--hexa-text-muted)",
                  animation: "hexa-blink 1s step-start infinite",
                }}
              />
            )}
          </>
        )}

        {/* Tool execution status — shown only while streaming with an active tool */}
        {showToolBadge && (
          <div
            className="flex items-center gap-2"
            style={{
              fontSize: "11px",
              color: "var(--hexa-text-muted)",
              fontStyle: "italic",
              marginTop: content ? "6px" : "0",
            }}
          >
            <span
              className="inline-block rounded-full"
              style={{
                width: 6,
                height: 6,
                backgroundColor: "var(--hexa-purple)",
                animation: "hexa-pulse-dot 1.2s ease-in-out infinite",
                flexShrink: 0,
              }}
            />
            {toolLabel(toolCall!)}...
          </div>
        )}

        {/* Attachments */}
        {attachments && attachments.length > 0 && (
          <div
            className="flex flex-col gap-1.5"
            style={{ marginTop: content ? "8px" : "0" }}
          >
            {attachments.map((att, i) => (
              <div key={`${att.name}-${i}`}>
                {/* Image thumbnail */}
                {att.type.startsWith("image/") && att.url && (
                  <img
                    src={att.url}
                    alt={att.name}
                    style={{
                      maxWidth: "200px",
                      maxHeight: "150px",
                      borderRadius: "8px",
                      marginBottom: "4px",
                      objectFit: "cover",
                    }}
                  />
                )}
                {/* File info row */}
                <div
                  className="flex items-center gap-1.5"
                  style={{
                    fontSize: "11px",
                    opacity: 0.8,
                  }}
                >
                  <AttachmentIcon type={att.type} />
                  <span>{truncateName(att.name)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <span
        className="mt-0.5 px-1"
        style={{
          fontSize: "10px",
          color: "var(--hexa-text-muted)",
          alignSelf: isUser ? "flex-end" : "flex-start",
        }}
      >
        {format(timestamp, "HH:mm", { locale: it })}
      </span>
    </div>
  )
}
